import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Paper, Modal, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { getConsentCookie, initialCookieValues, OPT_IN } from './cookies';
import ConsentStartScreen from './ConsentStartScreen';
import ConsentCustomizeScreen from './ConsentCustomizeScreen';
import { tcfApi } from './iab-tcf';

const useStyles = makeStyles(theme => ({
  modalContainer: {
    overflowY: 'auto',
    padding: theme.spacing(4),
    margin: '0 auto',
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    maxHeight: '90vh',
    maxWidth: 600,
    boxShadow: theme.shadows[3],
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2.5),
      margin: theme.spacing(0, 2.5),
    },
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
  },
}));

/**
 * Consent modal with OneTrust cookies and IAB TCF support
 *
 * OneTrust
 * @see https://developer.onetrust.com/onetrust/docs/when-consent-changes-new
 * - cookies: C0001-C0005
 *
 * IAB TCF
 * @see https://github.com/InteractiveAdvertisingBureau/iabtcf-es/tree/master/modules/core
 * @see https://github.com/InteractiveAdvertisingBureau/iabtcf-es/tree/master/modules/cmpapi
 * - vendor consent = vendor opt in + all vendor declared purposes and legitimate interests
 */
function ConsentModal({ open, onClose }) {
  const classes = useStyles();

  const [activeScreen, setActiveScreen] = useState('start');
  const [selectedCookies, setSelectedCookies] = useState({
    ...initialCookieValues,
  });

  // init tcf api and set state from cookie value
  useEffect(() => {
    const init = async () => {
      await tcfApi.initializeTCF();

      const cookieValue = getConsentCookie();

      if (!cookieValue) {
        tcfApi.resetAll();
      }

      if (cookieValue) {
        const cookieObject = cookieValue
          .split('=')[2]
          .split('%2C')
          .reduce((acc, item) => {
            const [key, value] = item.split('%3A');
            acc[key] = Boolean(value === OPT_IN);
            return acc;
          }, {});

        setSelectedCookies(cookieObject);

        if (Object.values(cookieObject).some(value => value)) {
          setActiveScreen('customize');
        }
      }
    };

    init();
  }, []);

  return (
    <Modal open={open} onClose={onClose} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Paper className={classes.modalContainer}>
        <div className={classes.modalHeader}>
          <Typography variant="h2" className={classes.title}>
            Cookie Preferences
          </Typography>
          <IconButton aria-label="close" data-test-id="consent-modal-close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {activeScreen === 'start' && (
          <ConsentStartScreen
            setActiveScreen={setActiveScreen}
            setSelectedCookies={setSelectedCookies}
            onClose={onClose}
          />
        )}
        {activeScreen === 'customize' && (
          <ConsentCustomizeScreen
            setActiveScreen={setActiveScreen}
            selectedCookies={selectedCookies}
            setSelectedCookies={setSelectedCookies}
            onClose={onClose}
          />
        )}
      </Paper>
    </Modal>
  );
}

ConsentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ConsentModal;
